import "./tournament-messages.scss";
import {
  faPlus,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  GSInput,
  GSItemList,
  GSListPage,
  GSEmptyList,
  GSActionBar,
  GSInfoCard,
  defaultBorders,
  golfstatusColors,
} from "golfstatus_react_components";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useMatch, useNavigate, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentMessages,
  selectMessages,
  selectMessagesNotifications,
  selectMessagesLoading,
  clearNotifications,
} from "../../../reducers/messagesSlice";
import TournamentMessageCard from "./tournament-message-card";
import { useCallbackNotificationAction } from "../../../hooks/notificationHooks";

const TournamentMessages = () => {
  const [search, setSearch] = useState("");
  const match = useMatch("/:tournamentID/messages/*");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const messages = useSelector(selectMessages);
  const notifications = useSelector(selectMessagesNotifications);
  const loading = useSelector(selectMessagesLoading);

  const refresh = useCallback(() => {
    dispatch(getTournamentMessages(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  const messageNotifications = useMemo(
    () => notifications?.filter?.((n) => n?.name === "Message"),
    [notifications]
  );

  useCallbackNotificationAction("saved", messageNotifications, refresh);
  useCallbackNotificationAction("created", messageNotifications, refresh);
  useCallbackNotificationAction("removed", messageNotifications, refresh);

  useEffect(() => {
    dispatch(getTournamentMessages(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  const filteredMessages = useMemo(() => {
    if (!messages?.length) return [];
    if (!search) return messages;
    return messages.filter((m) =>
      m?.subject?.toLowerCase?.()?.includes?.(search?.toLowerCase())
    );
  }, [messages, search]);

  const scheduledMessages = useMemo(
    () => filteredMessages.filter((m) => m?.status === "scheduled"),
    [filteredMessages]
  );

  const draftMessages = useMemo(
    () => filteredMessages.filter((m) => m?.status === "draft"),
    [filteredMessages]
  );

  const sentMessages = useMemo(
    () => filteredMessages.filter((m) => m?.status === "sent" || m?.status === "enqueued"),
    [filteredMessages]
  );

  const newMessage = () => {
    dispatch(clearNotifications());
    navigate("new");
  };

  const clearSearch = () => {
    setSearch("");
  };

  const openMessage = (item) => {
    if (item?.disabled === true) return;
    if (isSidePanelOpen()) {
      navigate(`/${match.params.tournamentID}/messages`);
      return;
    }
    dispatch(clearNotifications());
    navigate(`${item.id}`);
  };

  const getPageActions = () => {
    return [
      {
        id: 1,
        buttonTitle: "New Message",
        actionClick: newMessage,
        type: "black mobile-icon",
        actionIcon: faPlus,
      },
    ];
  };

  const getSearchActions = () => {
    return [];
  };

  const getDefaultSearch = () => {
    return (
      <GSInput
        textValue={search}
        leftIcon={faSearch}
        rightIcon={faTimesCircle}
        rightIconClick={clearSearch}
        placeholder="Search Messages..."
        onChange={(e) => {
          setSearch(e.target.value);
        }}
      />
    );
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Send a Message"
        detail="Get in touch with your people!"
        actions={[
          {
            title: "New Message",
            buttonIcon: faPlus,
            onClick: newMessage,
            type: "black",
          },
        ]}
      />
    );
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn't find the message you're looking for..."
        detail="Try changing the search term."
        actions={[
          {
            title: "Clear Search",
            onClick: clearSearch,
            type: "light-grey",
          },
          {
            title: "New Message",
            buttonIcon: faPlus,
            onClick: newMessage,
            type: "black",
          },
        ]}
      />
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const groupConfigs = [
    {
      items: scheduledMessages,
      header: "Scheduled Messages",
      type: "horizontal x-large-pad large-gap",
      listStyle: { overflow: "auto" },
    },
    {
      items: draftMessages,
      header: "Draft Messages",
      type: "horizontal x-large-pad large-gap",
      listStyle: { overflow: "auto" },
    },
    {
      items: sentMessages,
      header: "Sent Messages",
      type: "vertical list-item",
      listType: "list",
    },
  ];

  const getGroups = () =>
    groupConfigs
      .filter((g) => g.items?.length > 0)
      .map(({ items, header, type, listStyle, listType }) => ({
        list: (
          <div className="message-group">
            <GSActionBar header={header} />
            <GSItemList
              listStyle={listStyle}
              type={type}
              items={items}
              itemSelected={openMessage}
              itemStyle={
                listType === "list"
                  ? undefined
                  : { ...defaultBorders.mediumLargeBorderRadius }
              }
              listItem={(item) => {
                const isItemDisabled = item?.disabled === true;
                if (listType === "list") {
                  return (
                    <GSInfoCard
                      selectable={!isItemDisabled}
                      style={{
                        width: "100%",
                        boxShadow: "none",
                        borderRadius: 0,
                        borderBottom: `1px solid ${golfstatusColors.grey200}`,
                      }}
                      layout={<TournamentMessageCard {...item} />}
                    />
                  );
                }
                return (
                  <GSInfoCard
                    size="large"
                    selectable={!isItemDisabled}
                    style={
                      isItemDisabled
                        ? {
                            backgroundColor: golfstatusColors.grey200,
                            boxShadow: "none",
                          }
                        : undefined
                    }
                    layout={<TournamentMessageCard {...item} />}
                  />
                );
              }}
            />
          </div>
        ),
      }));

  const getItemList = () => {
    return (
      <GSItemList
        type="vertical all-messages"
        items={getGroups()}
        listItem={(group) => group.list}
        loading={loading?.length > 0}
        loadingMainText="Loading Messages..."
        emptyMessage={
          search !== "" && !loading?.length > 0
            ? getEmptySearchMessage()
            : getEmptyListMessage()
        }
      />
    );
  };

  return (
    <tournament-messages>
      <GSListPage
        title="Messages"
        getDefaultSearch={getDefaultSearch}
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={getItemList}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
      />
    </tournament-messages>
  );
};

export default TournamentMessages;
